import {Component, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import VideoState from '../shared/stores/root-store/video.state';
import {
    selectActualVideoState,
    selectAudioTracksFromState,
    selectButtonOnLastScene,
    selectIsCurrentSceneLoaded, selectIsInMobilePreviewMode,
    selectNavigateToTimeFromState, selectPreviewMode
} from '../shared/stores/root-store/selectors/video.selector';
import {SetVideoStateTypeAction} from '../shared/stores/root-store/actions/video.action';
import {VideoStateEnum} from '../../enums/video-state.enum';
import {BrowserService} from '../shared/services/browser.service';
import {combineLatest} from 'rxjs';
import {AudioService} from '../shared/services/audio.service';
import {BufferService} from '../shared/services/buffer.service';
import {selectImagesPreloaded} from '../shared/stores/root-store/selectors/overlay.selector';
import {ActivatedRoute} from '@angular/router';

@Component({
    selector: 'eocm-buffer',
    templateUrl: './buffer.component.html',
    styleUrls: ['./buffer.component.scss']
})
export class BufferComponent implements OnInit {
    isSplashScreenVisible = false;
    isLoadingIconVisible = true;
    isLoadedForFirstTime = true;
    lastVideoState: VideoStateEnum;
    isInteraction: boolean;
    isVideoPlaying = false;
    isButtonOnLastScene = false;
    isInPreviewMode = false;
    isInMobilePreviewMode = false;
    videoState;
    private noSplashScreen = false;

    constructor(
        private store: Store<{ video: VideoState }>,
        private browser: BrowserService,
        private audio: AudioService,
        private bufferService: BufferService,
        private activatedRoute: ActivatedRoute
    ) {
    }

    ngOnInit() {
        this.noSplashScreen = !!this.activatedRoute.snapshot.queryParamMap.get('nosplash');

        this.store.select(selectPreviewMode).subscribe(preview => {
            this.isInPreviewMode = preview;
        });

        combineLatest([
            this.store.select(selectActualVideoState),
            this.store.select(selectIsCurrentSceneLoaded),
            this.store.select(selectAudioTracksFromState),
            this.store.select(selectNavigateToTimeFromState),
            this.store.select(selectImagesPreloaded)
        ])
            .subscribe(([currentState, isCurrentSceneLoaded, audioTracks, navigateTo, imagesPreloaded]) => {
                this.isSplashScreenVisible = this.shouldShowSplashScreen(currentState);
                this.isVideoPlaying = currentState === VideoStateEnum.Playing;
                this.videoState = currentState;
                if (currentState !== VideoStateEnum.Buffering && currentState !== VideoStateEnum.Interaction) {
                    this.lastVideoState = currentState;
                }
                this.toggleLoadingIconVisibility(
                    isCurrentSceneLoaded,
                    this.audio.isLoaded(audioTracks),
                    currentState,
                    navigateTo,
                    imagesPreloaded
                );
                this.showLoadingIconForFirstTimeLoadedOnApple(currentState);
            });

        this.store.select(selectButtonOnLastScene).subscribe(buttons => {
            this.isButtonOnLastScene = buttons.length > 0;
        });

        this.store.select(selectIsInMobilePreviewMode).subscribe((isInMobilePreviewMode) => {
            this.isInMobilePreviewMode = isInMobilePreviewMode;
        });

        this.isInteractionState();
    }

    playVideo() {
        this.bufferService.playVideo();
    }

    private toggleLoadingIconVisibility(
        isCurrentSceneLoaded: boolean,
        isAudioLoaded: boolean,
        currentState: VideoStateEnum,
        navigateTo: number,
        imagesPreloaded: boolean
    ) {
        if (this.isLoadedForFirstTime && isCurrentSceneLoaded && isAudioLoaded && imagesPreloaded) {
            this.store.dispatch(SetVideoStateTypeAction({
                videoState: VideoStateEnum.UserPause
            }));
            this.isLoadedForFirstTime = false;
            this.autoStartOnPreview();
        }

        if (isCurrentSceneLoaded && isAudioLoaded && imagesPreloaded) {
            this.isLoadingIconVisible = false;

            if (navigateTo > 0 && currentState === VideoStateEnum.Buffering) {
                this.store.dispatch(SetVideoStateTypeAction({
                    videoState: this.lastVideoState
                }));
            }
        } else {
            this.isLoadingIconVisible = !(this.isLoadedForFirstTime && this.browser.isAppleMobileDevice());
        }
    }

    private showLoadingIconForFirstTimeLoadedOnApple(currentState: VideoStateEnum) {
        if (currentState === VideoStateEnum.Playing) {
            if (this.browser.isAppleMobileDevice() && this.isLoadedForFirstTime) {
                this.isLoadingIconVisible = true;
            }
            this.isLoadedForFirstTime = false;
        }
    }

    private shouldShowSplashScreen(currentState: VideoStateEnum): boolean {
        if (this.isVideoStopped(currentState)) {
            if (this.isVideoEnded(currentState)) {
                return false;
            }

            return !(currentState === VideoStateEnum.Buffering && this.browser.isAppleMobileDevice() && this.isInteraction);
        }

        return false;
    }

    private isInteractionState() {
        this.store.select(selectActualVideoState).subscribe((currentState) => {
            this.isInteraction = currentState === VideoStateEnum.Interaction;
        });
    }

    private autoStartOnPreview() {
        if (this.isInPreviewMode && !this.browser.isSafari()) {
            this.playVideo();
        }
    }

    private isVideoStopped(state: VideoStateEnum): boolean {
        return state === VideoStateEnum.Pause ||
      state === VideoStateEnum.UserPause ||
      (!this.isButtonOnLastScene && state === VideoStateEnum.Ended) ||
      state === VideoStateEnum.Buffering;
    }

    private isVideoEnded(state: VideoStateEnum): boolean {
        return state === VideoStateEnum.Ended;
    }
}
